// 👥 Employés E4b — Assistant « Nouvel employé » en 3 étapes (Identité → Emploi & rôles → Rémunération & invitation).
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, ArrowRight, UserPlus } from 'lucide-react';
import { ROLES_ATTRIBUABLES, libelleRole } from '@/lib/roles';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });
const ETAPES = ['Identité', 'Emploi & rôles', 'Rémunération & accès'];
const INITIAL = {
  prenom: '', nom: '', email: '', telephone: '', numero_employe: '', date_naissance: '', adresse: '', ville: '', code_postal: '',
  date_embauche: new Date().toISOString().slice(0, 10), statut: 'actif', permanence: 'permanent', type_employe: 'terrain', roles: ['peintre'], role_principal: 'peintre',
  salaires: { hors_decret: '', residentiel_leger: '', commercial_industriel: '', cout_employeur: '' }, inviter: false,
};

export function AssistantNouvelEmploye({ open, onClose, onCree, isProprietaire }) {
  const [etape, setEtape] = useState(0);
  const [f, setF] = useState(INITIAL);
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState(null);
  const maj = (patch) => { setF((x) => ({ ...x, ...patch })); setErreur(null); };
  const fermer = () => { setEtape(0); setF(INITIAL); setErreur(null); onClose(); };

  const toggleRole = (r) => {
    const roles = f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r];
    maj({ roles, role_principal: roles.includes(f.role_principal) ? f.role_principal : roles[0] || '' });
  };
  const valide = [() => f.prenom.trim() && f.nom.trim(), () => f.roles.length > 0 && !!f.role_principal, () => !f.inviter || !!f.email.trim()][etape]();

  const creer = async () => {
    setSaving(true);
    try {
      const salaires = Object.fromEntries(Object.entries(f.salaires).map(([k, v]) => [k, v === '' ? 0 : Number(v)]));
      const { inviter, ...payload } = f;
      const { data } = await axios.post(`${API_URL}/api/employees`, { ...payload, email: payload.email || null, numero_employe: payload.numero_employe || null, salaires, est_saisonnier: f.permanence === 'saisonnier' }, { headers: headers() });
      const emp = data.employee;
      if (inviter) {
        try {
          await axios.post(`${API_URL}/api/auth/invite-employee`, { email: emp.email, prenom: emp.prenom, nom: emp.nom, employee_id: emp.id }, { headers: headers() });
          toast.success('Invitation envoyée');
        } catch (e) { toast.error(e.response?.data?.detail || "Employé créé, mais l'invitation a échoué"); }
      }
      toast.success('Employé créé');
      onCree?.(emp); fermer();
    } catch (e) {
      const d = e.response?.data?.detail;
      setErreur(typeof d === 'string' ? d : Array.isArray(d) ? d.map((x) => x.msg).join(' ; ') : 'Erreur lors de la création');
      if (e.response?.status === 409) setEtape(0);
    } finally { setSaving(false); }
  };

  const Txt = (k, label, type = 'text', props = {}) => (
    <div><Label className="text-xs">{label}</Label><Input type={type} value={f[k]} onChange={(e) => maj({ [k]: e.target.value })} className="h-8" data-testid={`assistant-${k}`} {...props} /></div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) fermer(); }}>
      <DialogContent className="max-w-2xl" data-testid="assistant-nouvel-employe">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5" /> Nouvel employé</DialogTitle>
          <DialogDescription>Étape {etape + 1} sur 3 — {ETAPES[etape]}. Les autres renseignements se complètent ensuite, section par section, dans la fiche.</DialogDescription>
        </DialogHeader>
        <ol className="flex gap-2 text-xs" data-testid="assistant-etapes">
          {ETAPES.map((e, i) => <li key={e} className={`px-2 py-0.5 rounded-full ${i === etape ? 'bg-slate-800 text-white' : i < etape ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500'}`} data-testid={`assistant-etape-${i + 1}`}>{i + 1}. {e}</li>)}
        </ol>
        {erreur && <p className="text-sm text-red-600" data-testid="assistant-erreur">{erreur}</p>}

        {etape === 0 && (
          <div className="grid grid-cols-2 gap-3" data-testid="assistant-identite">
            {Txt('prenom', 'Prénom *')}{Txt('nom', 'Nom *')}{Txt('email', 'Courriel', 'email')}{Txt('telephone', 'Téléphone', 'tel')}
            {Txt('numero_employe', "Numéro d'employé")}{Txt('date_naissance', 'Date de naissance', 'date')}
            <div className="col-span-2">{Txt('adresse', 'Adresse')}</div>{Txt('ville', 'Ville')}{Txt('code_postal', 'Code postal')}
          </div>
        )}
        {etape === 1 && (
          <div className="space-y-3" data-testid="assistant-emploi">
            <div className="grid grid-cols-2 gap-3">
              {Txt('date_embauche', "Date d'embauche", 'date')}
              <div><Label className="text-xs">Type d'employé</Label>
                <Select value={f.type_employe} onValueChange={(v) => maj({ type_employe: v })}><SelectTrigger className="h-8" data-testid="assistant-type_employe"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="terrain">Terrain</SelectItem><SelectItem value="admin">Administration</SelectItem><SelectItem value="lmds">LMDS</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs">Statut</Label>
                <Select value={f.statut} onValueChange={(v) => maj({ statut: v })}><SelectTrigger className="h-8" data-testid="assistant-statut"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="actif">Actif</SelectItem><SelectItem value="saisonnier">Saisonnier</SelectItem><SelectItem value="temporaire">Temporaire</SelectItem><SelectItem value="sous_traitant">Sous-traitant</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs">Permanence</Label>
                <Select value={f.permanence} onValueChange={(v) => maj({ permanence: v })}><SelectTrigger className="h-8" data-testid="assistant-permanence"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="permanent">Permanent</SelectItem><SelectItem value="probation">Probation</SelectItem><SelectItem value="contractuel">Contractuel</SelectItem><SelectItem value="saisonnier">Saisonnier</SelectItem></SelectContent></Select></div>
            </div>
            <div>
              <Label className="text-xs">Rôles * (le département est dérivé des rôles)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {ROLES_ATTRIBUABLES.map((r) => (
                  <button type="button" key={r} onClick={() => toggleRole(r)} className={`text-xs px-2.5 py-1 rounded-full border ${f.roles.includes(r) ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700'}`} data-testid={`assistant-role-${r}`}>{libelleRole(r)}</button>
                ))}
              </div>
            </div>
            {f.roles.length > 1 && (
              <div><Label className="text-xs">Rôle principal</Label>
                <Select value={f.role_principal} onValueChange={(v) => maj({ role_principal: v })}><SelectTrigger className="h-8 w-60" data-testid="assistant-role_principal"><SelectValue /></SelectTrigger>
                  <SelectContent>{f.roles.map((r) => <SelectItem key={r} value={r}>{libelleRole(r)}</SelectItem>)}</SelectContent></Select></div>
            )}
          </div>
        )}
        {etape === 2 && (
          <div className="space-y-3" data-testid="assistant-remuneration">
            {isProprietaire ? (
              <div className="grid grid-cols-2 gap-3">
                {[['hors_decret', 'Hors décret ($/h)'], ['residentiel_leger', 'Résidentiel léger ($/h)'], ['commercial_industriel', 'Commercial / industriel ($/h)'], ['cout_employeur', 'Coût employeur ($/h)']].map(([k, l]) => (
                  <div key={k}><Label className="text-xs">{l}</Label><Input type="number" step="0.25" value={f.salaires[k]} onChange={(e) => maj({ salaires: { ...f.salaires, [k]: e.target.value } })} className="h-8" data-testid={`assistant-salaires-${k}`} /></div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-500">La rémunération se renseigne dans la fiche par le propriétaire.</p>}
            <label className="flex items-center gap-2 text-sm border rounded-lg p-3 bg-slate-50">
              <input type="checkbox" checked={f.inviter} onChange={(e) => maj({ inviter: e.target.checked })} data-testid="assistant-inviter" />
              Inviter sur la plateforme dès la création (courriel requis{f.email ? ` : ${f.email}` : ''})
            </label>
          </div>
        )}

        <DialogFooter className="gap-2">
          {etape > 0 && <Button variant="outline" onClick={() => setEtape((e) => e - 1)} disabled={saving} data-testid="assistant-precedent"><ArrowLeft className="w-4 h-4 mr-1" /> Précédent</Button>}
          {etape < 2
            ? <Button onClick={() => setEtape((e) => e + 1)} disabled={!valide} data-testid="assistant-suivant">Suivant <ArrowRight className="w-4 h-4 ml-1" /></Button>
            : <Button onClick={creer} disabled={!valide || saving} data-testid="assistant-creer">{saving ? 'Création…' : "Créer l'employé"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
